import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { getItemAIAnalysisSummary, getShortUrlParse } from '@op/services';
import { useEffect, useMemo, useState } from 'react';
import { PageFilterKeys } from '@op/components/FilterCommon/types';
import { saveShortUrlInfo } from '@op/components/FilterCommon/utils';
import {
  GptOptionsType, 
  GPTType,
  PromptConfirmItemType,
  StatusType,
} from '@op/components/GPT/types';
import { sortArrayAuxiliaryFunc } from '@op/utils';
import { GPTStatus, GPTStatusEmptyText } from '@op/constants/GPT';
import { DEFAULT_MODAL } from '@op/components/GPT/constants';

export function useShortUrlFilterValue({
  id,
  alwaysLoading = false,
}: {
  id: string;
  alwaysLoading?: boolean;
}) {
  const [loading, setLoading] = useState(true);
  const [pageName, setPageName] = useState<PageFilterKeys | undefined>(
    undefined,
  );
  const [filter, setFilter] = useState<any>({});

  function fetchData(id: string) {
    setLoading(true);
    getShortUrlParse(id)
      .then((data: any) => {
        const { PAGE_NAME, ...restFilter } = data || {};

        saveShortUrlInfo(id, JSON.stringify(data));

        setFilter(restFilter);
        setPageName(PAGE_NAME || '');
      })
      .finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    if (id) {
      fetchData(id);
    } else if (!alwaysLoading) {
      setLoading(false);
    }
  }, [id]);

  return { loading, filter, pageName };
}

type OneTimeType = {
  short_url: string;
  task_id: string;
  save_name: string;
  task_type: number;
  model_type?: GPTType;
};

type SubscriptType = {
  id: string;
  short_url: string;
  interval_unit: string;
  name: string;
  name_cn: string;
  last_finish_execution: {
    model_type: GPTType;
    task_id: string;
    task_type: GptOptionsType;
  };
};

export function usePageInfoValue({
  id,
  gameInfo,
  isSubscript,
}: {
  id: string;
  gameInfo: any;
  isSubscript: boolean;
}) {
  const [pageName, setPageName] = useState<string>('');
  const [pageNameCN, setPageNameCN] = useState<string>('');

  const { wrapperRequestTile, loading, error } = useCancelRequestHook({
    defaultLoading: false,
  });
  const [shortId, setShortId] = useState('');
  const [reportItem, setReportItem] = useState({});

  const [promptInfo, setPromptInfo] = useState<PromptConfirmItemType>({});

  function initOneTime(
    data: OneTimeType = {
      short_url: '',
      task_id: '',
      save_name: '',
      task_type: 3,
    },
  ) {
    setShortId(data.short_url);
    setPageName(data.save_name);
    setPromptInfo({
      modelType: data.model_type,
      resultType: 'page',
      gptOptions: data.task_type === 2 ? 'advanced' : 'basic',
      taskId: data.task_id,
    });
    setPageNameCN(data.save_name);
    setReportItem(data);
  }

  function initSubscript(
    data: SubscriptType = {
      id: '',
      short_url: '',
      interval_unit: '',
      name: '',
      name_cn: '',
      last_finish_execution: {
        task_id: '',
        task_type: 'basic',
        model_type: DEFAULT_MODAL,
      },
    },
  ) {
    setReportItem(data);
    const obj = data?.last_finish_execution || {};
    setPromptInfo({
      modelType: obj.model_type,
      resultType: 'page',
      gptOptions: obj.task_type,
      taskId: obj.model_type,
    });
    setShortId(data?.short_url);
    setPageName(data?.name);
    setPageNameCN(data?.name_cn || data?.name);
  }

  async function fetchData(id: string) {
    wrapperRequestTile(getItemAIAnalysisSummary, {
      id: Number(id),
      ...gameInfo,
      summary_type: isSubscript ? 'repeat_summary_task' : 'saved_summary',
    }).then((data: any) => {
      isSubscript ? initSubscript(data) : initOneTime(data);
    });
  }

  useEffect(() => {
    if (id) {
      fetchData(id);
    }
  }, [id]);

  return { loading, pageName, pageNameCN, shortId, promptInfo, reportItem };
}

export function useChannelShow() {
  const [commentList, setCommentList] = useState([]);
  const [socialList, setSocialList] = useState([]);
  const CHANNEL_SORTING = [
    'douyin',
    'youtube',
    'twitter',
    'instagram',
    'facebook',
    'tiktok',
    'bilibili',
    'reddit',
    'vk',
  ];
  function backCondOriginData(value: any) {
    const social = value?.channel?.social || [];
    const comments = value?.channel?.comments || [];
    setCommentList(comments.sort(sortArrayAuxiliaryFunc(CHANNEL_SORTING)));
    setSocialList(social.sort(sortArrayAuxiliaryFunc(CHANNEL_SORTING)));
  }

  return {
    commentList,
    socialList,
    backCondOriginData,
  };
}

export function useEmptyText({
  promptInfo,
}: {
  promptInfo: { status?: string };
}) {
  const getGPTEmptyProps = (info: { status?: string }) => {
    const { text } = GPTStatus?.[info?.status || 'init'] || {};
    const hasEmptyImg = text !== 'Summary failed';
    const hasEmptyTitle = text !== 'Summary failed';
    return {
      emptyDescText: GPTStatusEmptyText?.[text],
      hasEmptyImg,
      hasEmptyTitle,
      emptyTitleText: 'No data yet',
    };
  };

  const emptyProps = useMemo(() => getGPTEmptyProps(promptInfo), [promptInfo]);

  return {emptyProps, getGPTEmptyProps};
}
